import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Shield, ChevronDown, Lock, ShieldCheck, Users, Package,
  Landmark, Gem, Layers, Zap, BrainCircuit, Eye, EyeOff,
  AlertCircle, ArrowRight, CheckCircle2, ShieldX, XCircle,
  ArrowDown, Sparkles, Menu, X as XClose
} from "lucide-react";
import EarlyAccessBanner from "@/components/shared/EarlyAccessBanner";

/* ── helpers ────────────────────────────────────────────── */
function useInView(ref, threshold = 0.25) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return inView;
}

function useCountUp(end, duration, start) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    const t0 = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 4);
      setVal(Math.floor(end * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [end, duration, start]);
  return val;
}

const TOTAL_BALANCE = 227400;
const SEGMENTS = [
  { label: "Tax Custody", sub: "Government's Money", amount: 23400, color: "#EF4444", icon: ShieldCheck },
  { label: "Supplier Obligation", sub: "Suppliers' Money", amount: 31800, color: "#F59E0B", icon: Package },
  { label: "Customer Trust", sub: "Customers' Deposits", amount: 18500, color: "#3B82F6", icon: Users },
  { label: "Inventory Custody", sub: "Unsettled Goods", amount: 12400, color: "#8B5CF6", icon: Package },
  { label: "Operating Custody", sub: "Lenders' Capital", amount: 20700, color: "#6366F1", icon: Landmark },
  { label: "Free Equity", sub: "YOUR Money", amount: 120500, color: "#FFD700", icon: Gem },
];

const VAULTS_INFO = [
  { icon: ShieldCheck, label: "Tax Custody Vault", desc: "VAT and tax funds locked for the government. Never spendable.", color: "#EF4444", permission: "PROHIBITED" },
  { icon: Package, label: "Supplier Obligation", desc: "Trade credit reserved for supplier payments only.", color: "#F59E0B", permission: "RESTRICTED" },
  { icon: Users, label: "Customer Trust", desc: "Deposits held in trust until delivery is confirmed.", color: "#3B82F6", permission: "PROHIBITED" },
  { icon: Package, label: "Inventory Custody", desc: "Value of goods received but not yet settled.", color: "#8B5CF6", permission: "RESTRICTED" },
  { icon: Landmark, label: "Operating Custody", desc: "Loan proceeds — usable but not owned.", color: "#6366F1", permission: "RESTRICTED" },
  { icon: Gem, label: "Free Equity", desc: "True owned surplus. Freely spendable within buffers.", color: "#FFD700", permission: "FREE" },
];

const LOGO_URL_LANDING = "https://customer-assets.emergentagent.com/job_vault-truth/artifacts/s3q6fukn_image.png";

const CITIES = [
  { name: "London", utcOffset: 0,
    day: "https://images.unsplash.com/photo-1558958081-2e71529b4a69?w=1600&q=50",
    night: "https://images.unsplash.com/photo-1663669719518-4d4ec6f61192?w=1600&q=50" },
  { name: "Frankfurt", utcOffset: 1,
    day: "https://images.unsplash.com/photo-1564613655919-3b475e66f0ae?w=1600&q=50",
    night: "https://images.unsplash.com/photo-1564613655919-3b475e66f0ae?w=1600&q=50" },
  { name: "Dubai", utcOffset: 4,
    day: "https://images.unsplash.com/photo-1651284819032-42a925c1b114?w=1600&q=50",
    night: "https://images.unsplash.com/photo-1759176959174-465ad72f3c32?w=1600&q=50" },
  { name: "Madrid", utcOffset: 1,
    day: "https://images.unsplash.com/photo-1615479120360-b72f9dba252f?w=1600&q=50",
    night: "https://images.unsplash.com/photo-1612694882907-80f21c0e2bb7?w=1600&q=50" },
  { name: "Casablanca", utcOffset: 1,
    day: "https://images.unsplash.com/photo-1690298552540-e0caa713ebc1?w=1600&q=50",
    night: "https://images.unsplash.com/photo-1696989243191-b943c7b8a4ad?w=1600&q=50" },
  { name: "New York", utcOffset: -5,
    day: "https://images.unsplash.com/photo-1764863369407-ccccfbe8ab39?w=1600&q=50",
    night: "https://images.unsplash.com/photo-1644530777805-944f59551c4b?w=1600&q=50" },
  { name: "Sydney", utcOffset: 11,
    day: "https://images.unsplash.com/photo-1769981766147-c7c517606d31?w=1600&q=50",
    night: "https://images.unsplash.com/photo-1717884247018-5c380194f504?w=1600&q=50" },
  { name: "Hong Kong", utcOffset: 8,
    day: "https://images.unsplash.com/photo-1767854507041-5adb99368956?w=1600&q=50",
    night: "https://images.unsplash.com/photo-1600936240059-c953da0e6c21?w=1600&q=50" },
  { name: "Shenzhen", utcOffset: 8,
    day: "https://images.unsplash.com/photo-1587053267853-43fd39c80ed2?w=1600&q=50",
    night: "https://images.unsplash.com/photo-1654872592285-ed72535e4552?w=1600&q=50" },
  { name: "Los Angeles", utcOffset: -8,
    day: "https://images.unsplash.com/photo-1765873360301-a1ffdfa75818?w=1600&q=50",
    night: "https://images.unsplash.com/photo-1607505154912-59432ff1f615?w=1600&q=50" },
];

function getCityTimeInfo(city) {
  const now = new Date();
  const utcHour = now.getUTCHours();
  const localHour = (utcHour + city.utcOffset + 24) % 24;
  const isDaytime = localHour >= 6 && localHour < 20;
  const localMin = now.getUTCMinutes();
  const h12 = localHour % 12 || 12;
  const ampm = localHour < 12 ? "AM" : "PM";
  const timeStr = `${h12}:${String(localMin).padStart(2, "0")} ${ampm}`;
  return { isDaytime, localHour, timeStr, img: isDaytime ? city.day : city.night };
}

/* ── Landing Nav ────────────────────────────────────────── */
function LandingNav({ onScrollTo }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { token, user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  const handleNav = (anchor) => {
    setMobileOpen(false);
    onScrollTo(anchor);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#020617]/90 backdrop-blur-xl border-b border-slate-800/40 shadow-2xl' : 'bg-transparent'}`} data-testid="landing-nav">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid="landing-home-link">
          <img src="https://customer-assets.emergentagent.com/job_vault-truth/artifacts/s3q6fukn_image.png" alt="Sentient Vault" className="w-10 h-10 object-contain" />
          <div className="hidden sm:block">
            <span className="font-barlow font-bold text-xl text-slate-50 tracking-tight">SENTIENT VAULT</span>
            <span className="text-[8px] text-amber-400/60 tracking-[0.3em] uppercase block -mt-0.5 font-manrope">Obligation-Enforced Finance</span>
          </div>
        </a>
        {/* Desktop nav links */}
        <div className="hidden md:flex items-center gap-8">
          <button onClick={() => onScrollTo("solution")} className="text-xs text-slate-400 hover:text-slate-200 tracking-wider uppercase font-manrope transition-colors" data-testid="nav-how-it-works">How It Works</button>
          <button onClick={() => onScrollTo("features")} className="text-xs text-slate-400 hover:text-slate-200 tracking-wider uppercase font-manrope transition-colors" data-testid="nav-security">Security</button>
          <a href="/pricing" className="text-xs text-slate-400 hover:text-slate-200 tracking-wider uppercase font-manrope transition-colors" data-testid="nav-pricing">Pricing</a>
        </div>
        {/* Desktop CTAs */}
        {token ? (
          <Button onClick={() => navigate(user?.is_admin ? "/admin" : "/dashboard")} className="hidden md:inline-flex bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-900 font-semibold text-xs px-5 h-9 rounded-full shadow-lg shadow-amber-500/20 active:scale-[0.97] transition-all" data-testid="nav-dashboard-btn">
            Go to Dashboard
          </Button>
        ) : (
          <div className="hidden md:flex items-center gap-3">
            <a href="/login" className="text-xs text-slate-300 hover:text-amber-400 tracking-wider uppercase font-manrope transition-colors px-3 h-9 inline-flex items-center" data-testid="nav-sign-in-btn">
              Sign In
            </a>
            <a href="/register" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-900 font-semibold text-xs px-5 h-9 rounded-full shadow-lg shadow-amber-500/20 active:scale-[0.97] transition-all inline-flex items-center" data-testid="nav-start-trial-btn">
              Start Free Trial
            </a>
          </div>
        )}
        {/* Mobile menu trigger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          className="md:hidden text-slate-300 hover:text-amber-400 transition-colors"
          data-testid="nav-mobile-toggle"
        >
          {mobileOpen ? <XClose className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden border-t border-slate-800/40 bg-[#020617]/95 backdrop-blur-xl" data-testid="nav-mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-3">
            <button onClick={() => handleNav("solution")} className="text-sm text-slate-300 hover:text-amber-400 py-2 text-left tracking-wider uppercase font-manrope" data-testid="nav-mobile-how-it-works">How It Works</button>
            <button onClick={() => handleNav("features")} className="text-sm text-slate-300 hover:text-amber-400 py-2 text-left tracking-wider uppercase font-manrope" data-testid="nav-mobile-security">Security</button>
            <a href="/pricing" className="text-sm text-slate-300 hover:text-amber-400 py-2 tracking-wider uppercase font-manrope" data-testid="nav-mobile-pricing">Pricing</a>
            <div className="h-px bg-slate-800/60 my-1" />
            {token ? (
              <Button onClick={() => navigate(user?.is_admin ? "/admin" : "/dashboard")} className="bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold rounded-full h-10" data-testid="nav-mobile-dashboard-btn">
                Go to Dashboard
              </Button>
            ) : (
              <>
                <a href="/login" className="text-sm text-slate-300 hover:text-amber-400 py-2 tracking-wider uppercase font-manrope" data-testid="nav-mobile-sign-in">
                  Sign In
                </a>
                <a href="/register" className="bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold rounded-full h-10 inline-flex items-center justify-center text-sm" data-testid="nav-mobile-start-trial">
                  Start Free Trial
                </a>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

/* ── Hero Section ───────────────────────────────────────── */
function HeroSection({ onScrollTo }) {
  const ref = useRef();
  const inView = useInView(ref, 0.1);
  const [cityIdx, setCityIdx] = useState(0);
  const [cityInfos, setCityInfos] = useState(() => CITIES.map(getCityTimeInfo));

  useEffect(() => {
    const timer = setInterval(() => setCityIdx(i => (i + 1) % CITIES.length), 6000);
    return () => clearInterval(timer);
  }, []);

  // Refresh time info every minute
  useEffect(() => {
    const t = setInterval(() => setCityInfos(CITIES.map(getCityTimeInfo)), 60000);
    return () => clearInterval(t);
  }, []);

  const currentCity = CITIES[cityIdx];
  const currentInfo = cityInfos[cityIdx];

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      {/* City slideshow background — day/night based on local timezone */}
      {CITIES.map((city, i) => {
        const info = cityInfos[i];
        return (
          <img
            key={city.name}
            src={info.img}
            alt={`${city.name} ${info.isDaytime ? 'day' : 'night'}`}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[2000ms] ease-in-out"
            style={{ opacity: i === cityIdx ? 0.35 : 0, zIndex: 0 }}
            loading={i < 3 ? "eager" : "lazy"}
          />
        );
      })}
      <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/40 via-[#020617]/30 to-[#020617]/85 z-[1]" />

      {/* City label with live local time + day/night indicator */}
      <div className="absolute top-20 right-8 z-[2] flex items-center gap-2.5 px-3.5 py-2 rounded-xl border border-slate-700/40 bg-slate-900/50 backdrop-blur-md" data-testid="city-indicator">
        <div className={`w-2 h-2 rounded-full ${currentInfo.isDaytime ? 'bg-amber-400' : 'bg-cyan-400'}`} style={{ boxShadow: currentInfo.isDaytime ? '0 0 6px rgba(251,191,36,0.6)' : '0 0 6px rgba(6,182,212,0.6)' }} />
        <div className="flex flex-col">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-slate-300 font-mono tracking-wider uppercase font-semibold">{currentCity.name}</span>
            <span className="text-[8px] text-slate-600 font-mono">{cityIdx + 1}/{CITIES.length}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] text-slate-500 font-mono">{currentInfo.timeStr}</span>
            <span className={`text-[8px] font-mono px-1 py-px rounded ${currentInfo.isDaytime ? 'bg-amber-500/15 text-amber-400' : 'bg-cyan-500/15 text-cyan-400'}`}>
              {currentInfo.isDaytime ? 'DAY' : 'NIGHT'}
            </span>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32 w-full">
        <div className="max-w-3xl">
          {/* Expanded Logo + Brand */}
          <div className={`flex items-center gap-5 mb-10 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <img
              src={LOGO_URL_LANDING}
              alt="Sentient Vault"
              className="w-24 h-24 md:w-28 md:h-28 object-contain drop-shadow-[0_0_25px_rgba(255,215,0,0.3)]"
              style={{ animation: 'logo-pulse 5s ease-in-out infinite' }}
            />
            <div>
              <h2 className="font-barlow font-bold text-4xl md:text-5xl lg:text-6xl text-slate-50 tracking-tight leading-none">SENTIENT VAULT</h2>
              <span className="text-xs md:text-sm text-amber-400/70 tracking-[0.35em] uppercase font-manrope block mt-1">Obligation-Enforced Finance</span>
            </div>
          </div>

          {/* Eyebrow */}
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/20 bg-amber-500/5 mb-8 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-[10px] text-amber-400 font-mono tracking-wider uppercase">The financial truth system</span>
          </div>

          {/* Headline */}
          <h1 className={`font-barlow font-bold text-5xl sm:text-6xl lg:text-7xl text-slate-50 leading-[1.05] tracking-tight mb-6 transition-all duration-700 delay-150 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            Your business bank balance<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-300 to-amber-500">is lying to you.</span>
          </h1>

          {/* Subtext */}
          <p className={`text-lg md:text-xl text-slate-400 leading-relaxed max-w-xl mb-10 font-manrope transition-all duration-700 delay-300 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            Most businesses spend money that isn't theirs. They panic at VAT time. They default on suppliers despite strong sales. They appear profitable — then collapse in cash reality.
          </p>

          {/* CTAs */}
          <div className={`flex flex-wrap gap-4 mb-16 transition-all duration-700 delay-[450ms] ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <Button onClick={() => onScrollTo("problem")} className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-900 font-semibold px-8 h-12 rounded-full text-sm shadow-xl shadow-amber-500/25 active:scale-[0.97] transition-all gap-2" data-testid="hero-cta-btn">
              See the truth <ArrowDown className="w-4 h-4" />
            </Button>
            <a href="/demo" className="inline-flex items-center gap-2 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 h-12 rounded-full px-8 text-sm font-semibold transition-all" data-testid="hero-demo-btn">
              Try Demo <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {/* Stats */}
          <div className={`flex gap-10 transition-all duration-700 delay-[600ms] ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            {[
              { value: "73%", label: "of SMEs misallocate cash" },
              { value: "53%", label: "average true equity ratio" },
              { value: "6", label: "enforcement vaults" },
            ].map(s => (
              <div key={s.label}>
                <div className="font-mono text-2xl font-bold text-cyan-400">{s.value}</div>
                <div className="text-[10px] text-slate-600 uppercase tracking-wider mt-0.5 font-manrope">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-[9px] text-slate-600 uppercase tracking-[0.3em] font-manrope">Scroll</span>
        <ChevronDown className="w-4 h-4 text-slate-600" />
      </div>
    </section>
  );
}

/* ── Problem Section (The Illusion Model) ───────────────── */
function ProblemSection() {
  const ref = useRef();
  const inView = useInView(ref, 0.2);
  const [step, setStep] = useState(0);
  const animatedTotal = useCountUp(TOTAL_BALANCE, 1500, inView);

  useEffect(() => {
    if (!inView) return;
    const t1 = setTimeout(() => setStep(1), 800);
    const t2 = setTimeout(() => setStep(2), 2200);
    const t3 = setTimeout(() => setStep(3), 3000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [inView]);

  return (
    <section ref={ref} id="problem" className="relative py-24 md:py-32 overflow-hidden" data-testid="problem-section">
      <div className="absolute inset-0 bg-gradient-to-b from-[#020617] via-[#050D1A] to-[#020617]" />
      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-[10px] text-red-400 font-mono tracking-[0.3em] uppercase">The problem</span>
          <h2 className="font-barlow font-bold text-3xl md:text-4xl lg:text-5xl text-slate-50 mt-3">The Money Illusion</h2>
          <p className="text-base md:text-lg text-slate-500 mt-4 max-w-2xl mx-auto font-manrope">
            A business sees one number in their bank. But that number is a mixture of money belonging to the government, suppliers, customers, lenders — and only a fraction that's truly theirs.
          </p>
        </div>

        {/* The Visualization */}
        <div className="max-w-3xl mx-auto">
          {/* Bank Balance Display */}
          <div className={`text-center mb-8 transition-all duration-500 ${step >= 0 ? 'opacity-100' : 'opacity-0'}`}>
            <span className="text-xs text-slate-600 uppercase tracking-[0.2em] font-manrope">What your business bank shows</span>
            <div className="font-mono text-5xl md:text-6xl font-bold text-slate-50 mt-2 tabular">
              £{animatedTotal.toLocaleString()}
            </div>
          </div>

          {/* Illusion Bar → Truth Bar */}
          <div className="relative mb-6">
            {/* Step 1: Solid illusion bar */}
            <div className={`h-16 rounded-2xl bg-gradient-to-r from-slate-600 to-slate-500 transition-all duration-700 overflow-hidden ${step >= 1 ? 'opacity-100' : 'opacity-0'}`}>
              {step < 2 && (
                <div className="h-full flex items-center justify-center">
                  <span className="text-sm text-slate-300 font-mono tracking-wider">BANK BALANCE &mdash; LOOKS HEALTHY</span>
                </div>
              )}
              {/* Step 2: Segments reveal */}
              {step >= 2 && (
                <div className="h-full flex">
                  {SEGMENTS.map((seg, i) => {
                    const pct = (seg.amount / TOTAL_BALANCE) * 100;
                    const isEquity = seg.label === "Free Equity";
                    return (
                      <div
                        key={seg.label}
                        className="h-full flex items-center justify-center overflow-hidden relative transition-all duration-1000 ease-out"
                        style={{
                          width: step >= 2 ? `${pct}%` : '0%',
                          backgroundColor: seg.color,
                          transitionDelay: `${i * 120}ms`,
                          boxShadow: isEquity ? `0 0 30px ${seg.color}40` : 'none',
                        }}
                      >
                        {pct > 8 && (
                          <span className={`text-[9px] font-mono font-bold ${isEquity ? 'text-slate-900' : 'text-white/80'} whitespace-nowrap`}>
                            {Math.round(pct)}%
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Segment Labels */}
          <div className={`grid grid-cols-2 md:grid-cols-3 gap-3 mb-10 transition-all duration-700 ${step >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {SEGMENTS.map((seg, i) => {
              const isEquity = seg.label === "Free Equity";
              return (
                <div
                  key={seg.label}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-all duration-500 ${
                    isEquity ? 'border-amber-500/30 bg-amber-500/5 shadow-lg shadow-amber-500/5' : 'border-slate-800/40 bg-slate-900/30'
                  }`}
                  style={{ transitionDelay: `${i * 100 + 400}ms`, opacity: step >= 2 ? 1 : 0, transform: step >= 2 ? 'translateY(0)' : 'translateY(8px)' }}
                >
                  <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: seg.color, boxShadow: `0 0 8px ${seg.color}40` }} />
                  <div className="min-w-0">
                    <div className="text-[10px] text-slate-500 font-manrope truncate">{seg.sub}</div>
                    <div className={`text-sm font-mono font-semibold tabular ${isEquity ? 'text-amber-400' : 'text-slate-300'}`}>
                      £{seg.amount.toLocaleString()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* The Verdict */}
          <div className={`text-center transition-all duration-700 delay-500 ${step >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="inline-flex flex-col items-center p-6 rounded-2xl border border-amber-500/20 bg-amber-500/5">
              <span className="text-xs text-slate-500 uppercase tracking-[0.2em] font-manrope mb-2">The truth</span>
              <div className="font-barlow font-bold text-3xl md:text-4xl text-amber-400">
                Only 53% is truly yours.
              </div>
              <p className="text-sm text-slate-500 mt-2 max-w-md font-manrope">
                The rest belongs to the government, your suppliers, your customers, and your lenders. Traditional accounting records this — but never enforces it.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Solution Section (6 Vaults) ────────────────────────── */
function SolutionSection() {
  const ref = useRef();
  const inView = useInView(ref, 0.15);

  return (
    <section ref={ref} id="solution" className="relative py-24 md:py-32" data-testid="solution-section">
      <div className="absolute inset-0 bg-[#020617]" />
      {/* Subtle vault-like gradient overlay */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-amber-500/[0.02] blur-[120px]" />

      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8">
        <div className={`text-center mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-[10px] text-amber-400 font-mono tracking-[0.3em] uppercase">The solution</span>
          <h2 className="font-barlow font-bold text-3xl md:text-4xl lg:text-5xl text-slate-50 mt-3">
            Six Vaults. One Truth.
          </h2>
          <p className="text-base md:text-lg text-slate-500 mt-4 max-w-2xl mx-auto font-manrope">
            Sentient Vault analyses and classifies financial obligations by ownership, responsibility, and operational purpose. Every commitment surfaced before it becomes a risk.
          </p>
          {/* Non-custody trust statement — strategically critical: */}
          {/* every visitor must understand we don't hold their money. */}
          <div
            data-testid="non-custody-trust-card"
            className="mt-8 max-w-3xl mx-auto rounded-2xl border border-slate-800/60 bg-slate-900/50 p-5 sm:p-6 text-left"
          >
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
              <div className="space-y-1.5">
                <div className="text-sm font-semibold text-slate-200 font-manrope">
                  We do not custody or control your money.
                </div>
                <p className="text-sm text-slate-400 font-manrope leading-relaxed">
                  Sentient Vault analyses financial records, obligations,
                  liabilities, and business commitments to help companies
                  avoid hidden financial risk. We are not a bank, we are
                  not a payment processor, and we do not hold funds.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {VAULTS_INFO.map((vault, i) => {
            const Icon = vault.icon;
            const isFree = vault.permission === "FREE";
            return (
              <div
                key={vault.label}
                className={`group relative p-5 rounded-2xl border transition-all duration-500 hover:scale-[1.02] hover:shadow-xl ${
                  isFree ? 'border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-transparent hover:border-amber-500/50 hover:shadow-amber-500/10' :
                  'border-slate-800/50 bg-slate-900/30 hover:border-slate-700/60 hover:shadow-slate-800/20'
                }`}
                style={{
                  opacity: inView ? 1 : 0,
                  transform: inView ? 'translateY(0)' : 'translateY(16px)',
                  transition: `all 0.5s ease ${i * 100}ms`,
                }}
                data-testid={`vault-info-${vault.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${vault.color}15`, border: `1px solid ${vault.color}25` }}>
                    <Icon className="w-5 h-5" style={{ color: vault.color }} />
                  </div>
                  <div>
                    <h3 className={`font-barlow font-semibold text-base ${isFree ? 'text-amber-400' : 'text-slate-200'}`}>{vault.label}</h3>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed font-manrope">{vault.desc}</p>
                    <span className={`inline-flex items-center mt-2 text-[9px] font-mono px-2 py-0.5 rounded-full border ${
                      vault.permission === "FREE" ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" :
                      vault.permission === "PROHIBITED" ? "text-red-400 border-red-500/30 bg-red-500/10" :
                      "text-amber-400 border-amber-500/30 bg-amber-500/10"
                    }`}>
                      {vault.permission}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ── Features Section ───────────────────────────────────── */
function FeaturesSection() {
  const ref = useRef();
  const inView = useInView(ref, 0.15);

  const features = [
    {
      icon: Layers, color: "#06B6D4", title: "Value Bubble Ledger",
      desc: "Every invoice decomposed in real-time. See exactly how much is tax, supplier custody, overhead, and true profit — before a single payment is made.",
      detail: "\"This £5,250 payment is not profit. It is £250 tax, £3,000 supplier custody, and only £1,750 true profit.\"",
    },
    {
      icon: Zap, color: "#EF4444", title: "Guardrail Enforcement",
      desc: "Attempt to spend from Tax Custody? The system blocks it, simulates the consequences, and offers alternatives. Every override is logged.",
      detail: "When guardrails trigger, you see: \"DENIED — VAT shortfall projected in 22 days. Survival-mode behaviour detected.\"",
    },
    {
      icon: BrainCircuit, color: "#10B981", title: "Behavioural Intelligence",
      desc: "AI models whether your business operates in Survival, Emotional, or Strategic mode. It coaches you toward discipline — and praises you when you succeed.",
      detail: "\"Good job — you maintained full obligation coverage under stress. Strategic maturity improved 12% month-over-month.\"",
    },
  ];

  return (
    <section ref={ref} id="features" className="relative py-24 md:py-32" data-testid="features-section">
      <div className="absolute inset-0 bg-gradient-to-b from-[#020617] via-[#050D1A] to-[#020617]" />
      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8">
        <div className={`text-center mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-[10px] text-cyan-400 font-mono tracking-[0.3em] uppercase">Core engines</span>
          <h2 className="font-barlow font-bold text-3xl md:text-4xl lg:text-5xl text-slate-50 mt-3">Built for Financial Truth</h2>
        </div>

        <div className="space-y-6">
          {features.map((feat, i) => {
            const Icon = feat.icon;
            const isOdd = i % 2 !== 0;
            return (
              <div
                key={feat.title}
                className="group relative rounded-2xl border border-slate-800/40 bg-slate-900/20 p-6 md:p-8 hover:border-slate-700/50 transition-all duration-300 overflow-hidden"
                style={{
                  opacity: inView ? 1 : 0,
                  transform: inView ? 'translateY(0)' : 'translateY(20px)',
                  transition: `all 0.6s ease ${i * 150}ms`,
                }}
                data-testid={`feature-card-${feat.title.toLowerCase().replace(/\s/g, '-')}`}
              >
                {/* Glow accent */}
                <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-[0.03]" style={{ background: feat.color, filter: 'blur(60px)' }} />

                <div className={`flex flex-col md:flex-row items-start gap-6 ${isOdd ? 'md:flex-row-reverse' : ''}`}>
                  <div className="shrink-0">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${feat.color}12`, border: `1px solid ${feat.color}20` }}>
                      <Icon className="w-7 h-7" style={{ color: feat.color }} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-barlow font-bold text-xl text-slate-100 mb-2">{feat.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed font-manrope mb-4">{feat.desc}</p>
                    <div className="p-3 rounded-xl bg-slate-800/30 border border-slate-700/20">
                      <p className="text-xs text-slate-500 italic font-manrope leading-relaxed">{feat.detail}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ── Guardrail Demo Preview ─────────────────────────────── */
function GuardrailDemo() {
  const ref = useRef();
  const inView = useInView(ref, 0.2);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const t1 = setTimeout(() => setStep(1), 600);
    const t2 = setTimeout(() => setStep(2), 1800);
    const t3 = setTimeout(() => setStep(3), 3000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [inView]);

  return (
    <section ref={ref} className="relative py-24 md:py-32" data-testid="guardrail-demo-section">
      <div className="absolute inset-0 bg-[#020617]" />
      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8">
        <div className={`text-center mb-12 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-[10px] text-red-400 font-mono tracking-[0.3em] uppercase">Live demo</span>
          <h2 className="font-barlow font-bold text-3xl md:text-4xl text-slate-50 mt-3">What happens when you try to break the rules</h2>
        </div>

        {/* Simulated guardrail interaction */}
        <div className="max-w-xl mx-auto space-y-4">
          {/* Step 1: Attempt */}
          <div className={`p-4 rounded-xl border border-slate-800/50 bg-slate-900/40 transition-all duration-500 ${step >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center">
                <Landmark className="w-4 h-4 text-slate-400" />
              </div>
              <div>
                <div className="text-xs text-slate-500 font-manrope">Attempted action</div>
                <div className="text-sm text-slate-200 font-mono">SPEND £5,000 from Tax Custody Vault</div>
              </div>
            </div>
          </div>

          {/* Step 2: DENY */}
          <div className={`p-4 rounded-xl border transition-all duration-500 ${step >= 2 ? 'opacity-100 translate-y-0 border-red-500/30 bg-red-500/5' : 'opacity-0 translate-y-4 border-transparent'}`}>
            <div className="flex items-center gap-3 mb-3">
              <ShieldX className="w-6 h-6 text-red-400" />
              <span className="font-barlow font-bold text-xl text-red-400">DENIED</span>
            </div>
            <p className="text-xs text-slate-400 font-manrope mb-2">Tax Custody funds belong to the government and are prohibited from operational use.</p>
            <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/15">
              <span className="text-[10px] text-red-400 font-mono">VAT shortfall projected in 22 days | Coverage drops to 72%</span>
            </div>
          </div>

          {/* Step 3: Coaching */}
          <div className={`p-4 rounded-xl border transition-all duration-500 ${step >= 3 ? 'opacity-100 translate-y-0 border-amber-500/20 bg-amber-500/5' : 'opacity-0 translate-y-4 border-transparent'}`}>
            <div className="flex items-center gap-2 mb-2">
              <BrainCircuit className="w-4 h-4 text-amber-400" />
              <span className="text-[10px] text-amber-400 font-mono uppercase tracking-wider">Coaching response</span>
            </div>
            <p className="text-sm text-slate-400 font-manrope leading-relaxed">
              "Survival-mode behaviour detected. This override attempt has been logged. To protect cashflow and creditworthiness, prioritize obligations and preserve buffers."
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Login Section ──────────────────────────────────────── */
function LoginSection() {
  const { login, token, user } = useAuth();
  const navigate = useNavigate();
  const ref = useRef();
  const inView = useInView(ref, 0.15);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.user?.is_admin) {
        navigate("/admin", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch {
      setError("Invalid credentials. Check your email and password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section ref={ref} id="login" className="relative py-24 md:py-32" data-testid="login-section">
      <div className="absolute inset-0 bg-gradient-to-b from-[#020617] via-[#050D1A] to-[#020617]" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-amber-500/[0.02] blur-[100px]" />

      <div className="relative z-10 max-w-md mx-auto px-6 lg:px-8">
        <div className={`text-center mb-10 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <img src="https://customer-assets.emergentagent.com/job_vault-truth/artifacts/s3q6fukn_image.png" alt="Sentient Vault" className="w-16 h-16 mx-auto mb-5 drop-shadow-[0_0_20px_rgba(255,215,0,0.2)]" />
          <h2 className="font-barlow font-bold text-3xl md:text-4xl text-slate-50">
            {token ? "Welcome back." : "Ready to see your truth?"}
          </h2>
          <p className="text-sm text-slate-500 mt-3 font-manrope">
            {token ? "You're already signed in." : "Access Sentient Vault and discover what money is truly yours."}
          </p>
        </div>

        <div className={`p-6 rounded-2xl border border-slate-800/50 bg-slate-900/50 backdrop-blur-sm transition-all duration-700 delay-200 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {token ? (
            <div className="space-y-4 text-center">
              <p className="text-sm text-slate-400 font-manrope">
                Signed in as <span className="text-amber-400 font-semibold">{user?.email || "user"}</span>
              </p>
              <Button onClick={() => navigate(user?.is_admin ? "/admin" : "/dashboard")}
                className="w-full h-11 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-900 font-semibold rounded-xl shadow-lg shadow-amber-500/20 active:scale-[0.97] transition-all gap-2"
                data-testid="landing-go-dashboard-btn">
                <ArrowRight className="w-4 h-4" /> Go to Dashboard
              </Button>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="login-email" className="text-[10px] text-slate-500 uppercase tracking-wider">Email</Label>
                  <Input id="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                    className="bg-slate-800/50 border-slate-700 text-slate-100 h-11" data-testid="landing-login-email" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="login-pw" className="text-[10px] text-slate-500 uppercase tracking-wider">Password</Label>
                  <div className="relative">
                    <Input id="login-pw" type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                      className="bg-slate-800/50 border-slate-700 text-slate-100 h-11 pr-10" data-testid="landing-login-password" />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {error && (
                  <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="landing-login-error">
                    <AlertCircle className="w-4 h-4 shrink-0" />{error}
                  </div>
                )}
                <Button type="submit" disabled={loading}
                  className="w-full h-11 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-900 font-semibold rounded-xl shadow-lg shadow-amber-500/20 active:scale-[0.97] transition-all"
                  data-testid="landing-login-submit">
                  {loading ? "Authenticating..." : "Access Vault"}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <p className="text-sm text-slate-500">
                  No account? <a href="/register" className="text-amber-400 hover:text-amber-300 font-semibold">Start your 30-day free trial</a>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

/* ── Footer ─────────────────────────────────────────────── */
function Footer() {
  return (
    <footer className="border-t border-slate-800/30 bg-[#020617]">
      {/* Contact Section */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 text-center">
        <span className="text-[10px] text-slate-600 font-mono uppercase tracking-[0.2em]">Partnerships &middot; Investor Enquiries &middot; Pilot Access</span>
        <div className="mt-2">
          <a href="mailto:invitation@celestialmindsfze.com" className="text-sm text-amber-400/80 hover:text-amber-300 font-manrope tracking-wide transition-colors" data-testid="contact-email">
            invitation@celestialmindsfze.com
          </a>
        </div>
      </div>
      {/* Bottom Bar */}
      <div className="border-t border-slate-800/20 py-5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-amber-400/40" />
            <span className="text-xs text-slate-600 font-manrope">Sentient Vault™ — Obligation-Enforced Finance</span>
          </div>
          <div
            className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-manrope"
            data-testid="footer-early-access-tag"
          >
            <Sparkles className="w-3 h-3 text-amber-400/60" />
            <span className="text-amber-300/80">Early Development Preview</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[10px] text-slate-700 font-mono">Global Platform 2026</span>
            <span className="text-[10px] text-slate-700">&middot;</span>
            <span className="text-[10px] text-slate-600 font-manrope">A Celestial Minds FZE initiative. All rights reserved.</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ── Early Access Strip ────────────────────────────────── */
function EarlyAccessStrip() {
  return (
    <section
      className="relative bg-[#020617] border-t border-b border-slate-800/40 py-8"
      data-testid="landing-early-access-strip"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <EarlyAccessBanner testId="landing-early-access-banner" />
      </div>
    </section>
  );
}

/* ── Main Landing Page ──────────────────────────────────── */
export default function LandingPage() {
  const scrollTo = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  }, []);

  return (
    <div className="bg-[#020617] min-h-screen text-slate-50" data-testid="landing-page" style={{ scrollBehavior: 'smooth' }}>
      <LandingNav onScrollTo={scrollTo} />
      <HeroSection onScrollTo={scrollTo} />
      <EarlyAccessStrip />
      <ProblemSection />
      <SolutionSection />
      <FeaturesSection />
      <GuardrailDemo />
      <LoginSection />
      <Footer />
    </div>
  );
}
